import { useMemo } from 'react';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { useQueryState } from 'hooks/useQueryState';
import useUserHook from 'hooks/useUserHook';

import { useClusterRCFAnalysisStore } from 'store/clusterRCFAnalysis.store';

import { useQueryClustersData } from 'api/queries/clustersData.query';
import { useQueryClustersDataBySearchWord } from 'api/queries/clustersDataBySearchWord.query';
import { useQueryClustersList } from 'api/queries/clustersList.query';

import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';

import ClusterActivities from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/ClusterActivities/ClusterActivities';
import { prepareData } from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/ClusterRCFAnalysis.utils';
import ClusterStatistics from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/ClusterStatistics/ClusterStatistics';
import { compareOptions } from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/CompareTo/CompareTo';
import classes from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/GraphComponents.module.scss';
import Rcf from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/RCF/rcf';
import WhiskerPlot from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/WhiskerPlot/WhiskerPlot';
import { LoaderContainer } from 'components/common';

export const GraphComponents = ({ projectId, contractId, latestVersionId }) => {
    const idList = useMemo(() => (latestVersionId ? [latestVersionId] : []), [latestVersionId]);
    const [compare] = useQueryState('clusterScope');
    const compareKey = compareOptions.find((item) => item.value === compare)?.value;
    const isAutomatedClusteringOption = useClusterRCFAnalysisStore((store) => store.isAutomatedClusteringOption);
    const selectedClusterId = useClusterRCFAnalysisStore((store) => store.selectedClusterId);
    const searchWord = useClusterRCFAnalysisStore((store) => store.searchWord);
    const { customerId } = useUserHook();
    const isClustersList = useClusterRCFAnalysisStore((store) => store.isClustersList);

    const { data: clustersList, isFetching } = useQueryClustersList({ idList, projectId, compare: compareKey });

    const { data: clusterDataById, isLoading: isLoadingById } = useQueryClustersData({
        idList,
        clusterId: selectedClusterId?.clusterId,
        isAutomatedClusteringOption: isAutomatedClusteringOption,
        compare: compareKey,
    });

    const { data: clusterDataBySearchWord, isLoading: isLoadingSearch } = useQueryClustersDataBySearchWord({
        idList,
        compare: compareKey,
        projectId,
        customerId,
        searchWord,
    });

    const clusterData = useMemo(() => {
        return isAutomatedClusteringOption ? clusterDataById : clusterDataBySearchWord?.clusterData;
    }, [isAutomatedClusteringOption, clusterDataById, clusterDataBySearchWord]);

    const {
        clusterStatisticsData,
        durationDistributionData,
        RCFDurationData,
        RCFDurationRatioData,
        ClusterActivitiesData,
    } = prepareData({ clusterData });

    const isNoClustersFound = isAutomatedClusteringOption && !isClustersList;
    const isNoSearchData = !isAutomatedClusteringOption && clusterData?.data?.clusterActivities.length === 0;
    const isSearchExecuted = !isAutomatedClusteringOption && !!searchWord && !isLoadingSearch;
    const showAutomatedData = isAutomatedClusteringOption && (clusterData || isLoadingById);
    const showCustomData = !isNoSearchData && isSearchExecuted;

    const subTitle = useCustomPageSubTitle({
        projectId,
        contractId,
        latestVersionId: Array.isArray(idList) ? idList[0] : undefined,
    });

    return (
        <>
            {isFetching && !clustersList && (
                <LoaderContainer height={'500px'}>
                    <OverlayWithSpinner />
                </LoaderContainer>
            )}
            {!isFetching && clustersList && (
                <>
                    {isNoClustersFound && <NoData>No Clusters found for the selected program version</NoData>}
                    {isNoSearchData && <NoData>No Data found for this search</NoData>}
                    {(showAutomatedData || showCustomData) && (
                        <>
                            <div className={classes.topComponentsRow}>
                                <ClusterStatistics
                                    title="Cluster Statistics"
                                    tableData={clusterStatisticsData}
                                    tooltip={clusterData?.statistics?.info}
                                    subTitle={subTitle}
                                />
                                <WhiskerPlot
                                    plotData={durationDistributionData}
                                    tooltipArg={clusterData?.durationDistribution?.info}
                                    subTitle={subTitle}
                                />
                                <Rcf
                                    rcfDurationData={RCFDurationData}
                                    rcfDurationRatioData={RCFDurationRatioData}
                                    tooltipArg={clusterData?.rcf?.info}
                                    loadingCluster={isLoadingById || isLoadingSearch}
                                    subTitle={subTitle}
                                />
                            </div>

                            <div data-testid={'cluster-activities'} className={classes.activitiesRow}>
                                <ClusterActivities
                                    tableData={ClusterActivitiesData}
                                    tooltipArg={clusterData?.data?.info}
                                    idList={idList}
                                    compare={compare}
                                    subTitle={subTitle}
                                    totalActivities={clusterData?.statistics?.totalActivities}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
};
